/**
 * Settings → Audit log — the tenant-wide administrative record.
 *
 * Two tabs (2026-07-23 rebuild):
 *  • System — rows from the REAL `listStudioAuditEvents` op: server-side
 *    search/filter/pagination (50/page), sortable headers, clean timestamps
 *    via lib/datetime, per-entry detail (payload with PII warning for auth
 *    events, before/after snapshot for edits/deletes, actor + IP), raw JSON
 *    download, CSV (server op) / Excel / JSON export.
 *  • Graph API — every request to the Graph API (method, URL, status code,
 *    server response time, request/response bodies) via the DECLARED
 *    `listGraphApiRequestLogs` op (REQ-061; harness seed layer until it
 *    ships — see graph-api-preview-seeds.js). Method chips are color-coded;
 *    search covers actor, method, URL, status, and object type.
 *
 * No CD-authored data anywhere: System rows come from api-mocks' curated
 * seed; Graph rows from the __source-marked preview seed layer (REQ-061).
 * Entries whose fixtures lack payload/snapshot render an honest empty note.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import * as apiClient from "@v0y4ge/api-client";
import { Avatar, Btn, Card, Icon, Input, Menu, MenuItem, T, alpha } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import SettingsShell from "./SettingsShell";
import { StatTile } from "../../shell/composites";
import { DataSourceStrip } from "../../shell/opSource";
import { useApprovals } from "../approvals/approvals-store";
import { useT, t as i18nT } from "../../lib/i18n";
import { friendlyDateShort, friendlyTime } from "../../lib/datetime";
import { listGraphApiRequestLogs, type GraphApiMethod, type GraphApiRequestLog, type GraphApiRequestLogList } from "./audit-graph-api";

/* ── Row shapes ──────────────────────────────────────────────────────────── */
interface AuditActorInfo { name: string; init: string; color: string; email: string; role: string }
export interface AuditEntry {
  id: string; occurredAt: string; day: string; time: string;
  actor: AuditActorInfo;
  action: string; actionKey: string; category: string; target: string; ip: string;
  severity: "info" | "warn" | "critical";
  resourceType: string; resourceId: string | null;
  payload: unknown;
}
interface AuditSummary { entries?: number; flagged?: number; actors?: number; retentionDays?: number }
interface PageInfo { page: number; limit: number; total: number; hasMore: boolean }

function fromServer(r: Record<string, any>): AuditEntry {
  const actor = (r.actor ?? {}) as Record<string, any>;
  const sev = r.severity === "warn" || r.severity === "critical" ? r.severity : "info";
  return {
    id: String(r.id ?? ""),
    occurredAt: String(r.occurredAt ?? r.createdAt ?? ""),
    day: String(r.day ?? ""), time: String(r.time ?? ""),
    actor: {
      name: String(actor.name ?? "—"), init: String(actor.init ?? actor.initials ?? "?"),
      color: String(actor.color ?? T.p1), email: actor.email == null ? "" : String(actor.email),
      role: String(actor.role ?? r.actorRole ?? ""),
    },
    action: String(r.actionLabel ?? r.action ?? ""), actionKey: String(r.actionKey ?? r.action ?? ""),
    category: String(r.category ?? "Settings"), target: String(r.target ?? r.details ?? ""),
    ip: String(r.ip ?? r.sourceIp ?? "—"), severity: sev as AuditEntry["severity"],
    resourceType: String(r.resourceType ?? ""), resourceId: r.resourceId == null ? null : String(r.resourceId),
    payload: r.payload as unknown,
  };
}

const AUDIT_CATEGORY_COLOR: Record<string, string> = {
  Auth: T.tDarkBlue, Events: T.p1, Guests: T.tTurquoise, Comms: T.tPink,
  Roles: T.tOrange, Integrations: T.n2, Billing: T.green1, Branding: T.tPurple,
  Security: T.red1, Settings: T.soft,
  "On-site": "#2477A8", // ui-raw: fixed categorical-legend teal — every nearby jrni blue token is already claimed by another entry in this map (Auth=tDarkBlue); reused verbatim below for the GET/3xx legend
  Approvals: T.p1, // was literal #5142C7 — byte-identical to T.p1
};
const AUDIT_SEVERITY: Record<string, { label: string; labelKey: string; color: string }> = {
  info:     { label: "Info",     labelKey: "settings.auditLog.info",     color: T.n3 },
  warn:     { label: "Warning",  labelKey: "settings.auditLog.warning",  color: T.orange1 },
  critical: { label: "Critical", labelKey: "settings.auditLog.critical", color: T.red1 },
};
const METHOD_COLOR: Record<GraphApiMethod, string> = {
  // ui-raw: GET/POST/PUT keep their fixed HTTP-method legend hues (same reasoning
  // as the "On-site" category color above — no unclaimed jrni token matches
  // without colliding with another color already used in this file's legends).
  GET: "#2C8B57", POST: "#2477A8", PUT: "#E26F2C",
  PATCH: T.p1, // was literal #5142C7 — byte-identical to T.p1
  DELETE: T.red1,
};
const statusColor = (s: number): string => (
  s < 300 ? "#2C8B57" /* ui-raw: 2xx legend green, matches METHOD_COLOR.GET */ :
  s < 400 ? "#2477A8" /* ui-raw: 3xx legend teal, matches METHOD_COLOR.POST / "On-site" */ :
  s < 500 ? T.orange1 : T.red1
);

/* ── Payload helpers ─────────────────────────────────────────────────────── */
const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const hasPayload = (p: unknown): boolean => (isRecord(p) ? Object.keys(p).length > 0 : Array.isArray(p) ? p.length > 0 : p != null);
const SENSITIVE_RE = /log.?in|sign.?in|password|reset|mfa|session|token|api.?key/i;
const isSensitive = (e: AuditEntry): boolean =>
  e.category === "Auth" || e.category === "Security" || SENSITIVE_RE.test(e.actionKey) || SENSITIVE_RE.test(e.action);
const MUTATION_RE = /updat|edit|chang|delet|remov|renam|archiv/i;
const isMutation = (e: AuditEntry): boolean => MUTATION_RE.test(e.actionKey) || MUTATION_RE.test(e.action);
/** Before/after snapshot, when the server recorded one in the payload. */
function beforeAfter(p: unknown): { before: unknown; after: unknown } | null {
  if (!isRecord(p)) return null;
  if ("before" in p || "after" in p) return { before: p.before ?? null, after: p.after ?? null };
  return null;
}
const whenLabel = (e: AuditEntry): string =>
  e.occurredAt ? `${friendlyDateShort(e.occurredAt)} · ${friendlyTime(e.occurredAt)}` : `${e.day} ${e.time}`.trim();

/* ── Download / export builders (local file generation, no persistence) ─── */
function downloadBlob(name: string, mime: string, content: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
function csvOf(head: string[], rows: (string | number)[][]): string {
  const esc = (v: string | number) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return "\uFEFF" + [head, ...rows].map((r) => r.map(esc).join(",")).join("\n");
}
function xlsOf(sheet: string, head: string[], rows: (string | number)[][]): string {
  const escXml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const cell = (v: string | number) => (typeof v === "number" && Number.isFinite(v)
    ? `<Cell><Data ss:Type="Number">${v}</Data></Cell>`
    : `<Cell><Data ss:Type="String">${escXml(String(v ?? ""))}</Data></Cell>`);
  const row = (cells: (string | number)[]) => `<Row>${cells.map(cell).join("")}</Row>`;
  return `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="${escXml(sheet)}"><Table>${row(head)}${rows.map(row).join("")}</Table></Worksheet></Workbook>`;
}
const jsonPretty = (v: unknown): string => { try { return JSON.stringify(v, null, 2) ?? "null"; } catch { return String(v); } };

type ExportKind = "xls" | "csv" | "json";

/* ── Small shared pieces ─────────────────────────────────────────────────── */
function Th({ id, label, sort, onSort }: { id: string; label: string; sort: { key: string; dir: 1 | -1 }; onSort: (id: string) => void }) {
  const active = sort.key === id;
  return (
    // ui-raw: bespoke unstyled sort-toggle header (no padding/border/fixed height) —
    // Btn's geometry (fixed 28/36/44px height, padding, border, background) doesn't
    // match this inline uppercase-label-plus-chevron header-cell control.
    <button onClick={() => onSort(id)} style={{ /* ui-local sort toggle */
      background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: T.space[1],
      fontFamily: T.font, fontSize: T.text.label.fontSize, fontWeight: 600, letterSpacing: 0.04, textTransform: "uppercase",
      color: active ? T.n1 : T.soft, textAlign: "left",
    }}>
      {label}
      <span style={{ opacity: active ? 1 : 0.3, display: "inline-flex" }}><Icon.chev size={11} dir={active && sort.dir === 1 ? "up" : "down"} /></span>
    </button>
  );
}

function Pager({ pag, onPage }: { pag: PageInfo; onPage: (p: number) => void }) {
  const t = useT();
  const from = pag.total === 0 ? 0 : (pag.page - 1) * pag.limit + 1;
  const to = pag.total === 0 ? 0 : Math.min(pag.page * pag.limit, pag.total);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${T.space[3]} ${T.space[4]}`, borderTop: `1px solid ${T.border}`, background: T.cardSoft }}>
      <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>
        {t("settings.auditLog.showing", "Showing")}{" "}{from}–{to} {t("settings.auditLog.of", "of")}{" "}{pag.total}
      </span>
      <div style={{ display: "flex", gap: T.space[2] }}>
        <Btn size="sm" variant="secondary" disabled={pag.page <= 1} onClick={() => onPage(pag.page - 1)}>{t("settings.auditLog.previous", "Previous")}</Btn>
        <Btn size="sm" variant="secondary" disabled={!pag.hasMore} onClick={() => onPage(pag.page + 1)}>{t("settings.auditLog.next", "Next")}</Btn>
      </div>
    </div>
  );
}

function ExportMenu({ onPick, busy }: { onPick: (k: ExportKind) => void; busy: boolean }) {
  const t = useT();
  const badge = (txt: string, bg: string) => (
    <span style={{ width: 34, height: 20, borderRadius: T.radius.sm, background: bg, color: T.onColor, fontSize: T.text.label.fontSize, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", letterSpacing: 0.03, flexShrink: 0 }}>{txt}</span>
  );
  const itemLabel = (label: string, sub: string) => (
    <span style={{ display: "flex", flexDirection: "column", gap: T.space[1] }}>
      <span style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n1 }}>{label}</span>
      <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{sub}</span>
    </span>
  );
  return (
    <Menu
      align="end"
      minWidth={250}
      aria-label={t("settings.auditLog.export", "Export")}
      trigger={
        <Btn variant="secondary" icon={<Icon.download size={14} />} disabled={busy}>
          {busy ? t("settings.auditLog.exporting", "Exporting…") : t("settings.auditLog.export", "Export")}
        </Btn>
      }
    >
      {/* ui-raw: "#107C41" is Excel's own brand green (product-format recognition
          badge) — not a UI-palette color, no jrni token applies, kept literal per
          the map's brand-color exemption. */}
      <MenuItem onSelect={() => onPick("xls")} icon={badge("XLS", "#107C41")}>
        {itemLabel(t("settings.auditLog.excel", "Excel"), t("settings.auditLog.xlsWorkbookOpensInExcel", ".xls workbook · opens in Excel"))}
      </MenuItem>
      <MenuItem onSelect={() => onPick("csv")} icon={badge("CSV", T.n1)}>
        {itemLabel(t("settings.auditLog.csv", "CSV"), t("settings.auditLog.csvUtf8CommaDelimited", ".csv · UTF-8 · comma delimited"))}
      </MenuItem>
      <MenuItem onSelect={() => onPick("json")} icon={badge("{ }", T.p1)}>
        {itemLabel(t("settings.auditLog.rawJson", "Raw JSON"), t("settings.auditLog.jsonFullEntriesWithBodies", ".json · full entries incl. payloads/bodies"))}
      </MenuItem>
    </Menu>
  );
}

function JsonBox({ label, data, empty }: { label: string; data: unknown; empty: string }) {
  const has = hasPayload(data);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], minWidth: 0 }}>
      <span style={{ fontSize: T.text.label.fontSize, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", color: T.soft }}>{label}</span>
      {has ? (
        <pre style={{ margin: 0, padding: `${T.space[3]} ${T.space[3]}`, background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius.md, fontFamily: T.fontMono, fontSize: T.text.label.fontSize, lineHeight: 1.55, color: T.n1, maxHeight: 240, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{jsonPretty(data)}</pre>
      ) : (
        <div style={{ padding: `${T.space[3]} ${T.space[3]}`, background: T.white, border: `1px dashed ${T.border}`, borderRadius: T.radius.md, fontSize: T.text.label.fontSize, color: T.soft }}>{empty}</div>
      )}
    </div>
  );
}

function PiiBanner() {
  const t = useT();
  return (
    <div role="note" style={{ display: "flex", alignItems: "flex-start", gap: T.space[2], padding: `${T.space[2]} ${T.space[3]}`, borderRadius: T.radius.md, border: `1px dashed ${alpha(T.orange1, 55)}`, background: alpha(T.orange1, 8), fontSize: T.text.label.fontSize, color: T.n2, lineHeight: 1.45 }}>
      <span style={{ color: T.orange1, display: "inline-flex", flexShrink: 0, marginTop: T.space[1] }}><Icon.warn size={13} /></span>
      <span>
        <b style={{ color: T.n1 }}>{t("settings.auditLog.mayContainPersonalData", "May contain personal data (PII).")}</b>{" "}
        {t("settings.auditLog.handleAccordingToPrivacyPolicyViewingIsRecorded", "Handle this payload according to your privacy policy — viewing it is itself recorded in the audit log.")}
      </span>
    </div>
  );
}

function MetaItem({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[1], minWidth: 0 }}>
      <span style={{ fontSize: T.text.label.fontSize, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", color: T.soft }}>{label}</span>
      <span style={{ fontSize: T.text.bodySm.fontSize, color: T.n1, fontFamily: mono ? T.fontMono : T.font, wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

/* Tamper-evidence chain check (kit 7eb21218): `verifyAuditChain` — GET
 * /v1/admin/audit/verify over the canonical unified audit store. On-demand:
 * one awaited call per click, honest error state, result rendered as a chip. */
type ChainState = { kind: "idle" } | { kind: "busy" } | { kind: "ok"; checked: number } | { kind: "broken"; checked: number; brokenAt: string | null } | { kind: "err" };
function TamperCheck() {
  const t = useT();
  const [st, setSt] = useState<ChainState>({ kind: "idle" });
  const run = (): void => {
    const fn = (apiClient as Record<string, unknown>).verifyAuditChain;
    if (typeof fn !== "function") { setSt({ kind: "err" }); return; }
    setSt({ kind: "busy" });
    void (fn as (p?: Record<string, unknown>) => Promise<Record<string, any>>)({})
      .then((r) => setSt(r?.ok
        ? { kind: "ok", checked: Number(r.checked) || 0 }
        : { kind: "broken", checked: Number(r?.checked) || 0, brokenAt: r?.broken_at == null ? null : String(r.broken_at) }))
      .catch((e) => { console.warn("verifyAuditChain failed:", e); setSt({ kind: "err" }); });
  };
  const pill = (color: string, text: string) => (
    <span title={text} style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], padding: `${T.space[2]} ${T.space[3]}`, borderRadius: T.radius.pill, border: `1px solid ${alpha(color, 45)}`, background: alpha(color, 10), color, fontSize: T.text.label.fontSize, fontWeight: 700, maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      <span style={{ width: 7, height: 7, borderRadius: T.radius.pill, background: color, flexShrink: 0 }} />{text}
    </span>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
      {st.kind === "ok" ? pill(T.green1, `${t("settings.auditLog.chainVerified", "Chain verified")} · ${st.checked} ${t("settings.auditLog.rows", "rows")}`) : null}
      {st.kind === "broken" ? pill(T.red1, st.brokenAt ? `${t("settings.auditLog.chainBrokenAt", "Chain broken at")} ${st.brokenAt}` : t("settings.auditLog.chainBroken", "Chain broken")) : null}
      {st.kind === "err" ? pill(T.orange1, t("settings.auditLog.verificationUnavailable", "Verification unavailable")) : null}
      <Btn variant="secondary" disabled={st.kind === "busy"} onClick={run}>
        {st.kind === "busy" ? t("settings.auditLog.checking", "Checking…") : st.kind === "idle" ? t("settings.auditLog.tamperCheck", "Tamper check") : t("settings.auditLog.reCheck", "Re-check")}
      </Btn>
    </div>
  );
}

/* ── System tab rows ─────────────────────────────────────────────────────── */
const AUDIT_COLS = "180px 1.1fr 1.7fr 122px 116px 26px";

function AuditRow({ e, open, onToggle }: { e: AuditEntry; open: boolean; onToggle: () => void }) {
  const t = useT();
  const catColor = AUDIT_CATEGORY_COLOR[e.category] || T.soft;
  const sev = AUDIT_SEVERITY[e.severity] || AUDIT_SEVERITY.info;
  const ba = beforeAfter(e.payload);
  const sensitive = isSensitive(e);
  return (
    <>
      <div onClick={onToggle} style={{ display: "grid", gridTemplateColumns: AUDIT_COLS, padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, alignItems: "center", gap: T.space[2], cursor: "pointer", background: open ? T.cardSoft : T.white }}>
        <div style={{ display: "flex", alignItems: "center", gap: T.space[2], minWidth: 0 }}>
          <span title={t(sev.labelKey, sev.label)} style={{ width: 7, height: 7, borderRadius: T.radius.pill, background: sev.color, flexShrink: 0 }} />
          <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: T.n2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{whenLabel(e)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: T.space[2], minWidth: 0 }}>
          <Avatar size={26} initials={e.actor.init} color={e.actor.color} />
          <span style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.actor.name}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
            <span style={{ fontSize: T.text.bodySm.fontSize, color: T.n1, fontWeight: 500 }}>{e.action}</span>
            {sensitive && hasPayload(e.payload) ? <span style={{ fontSize: T.text.label.fontSize, fontWeight: 800, color: T.orange1, background: alpha(T.orange1, 12), borderRadius: T.radius.sm, padding: `${T.space[1]} ${T.space[2]}`, letterSpacing: 0.04 }}>{t("settings.auditLog.pii", "PII")}</span> : null}
            {ba ? <span style={{ fontSize: T.text.label.fontSize, fontWeight: 800, color: T.p1, background: T.p4, borderRadius: T.radius.sm, padding: `${T.space[1]} ${T.space[2]}`, letterSpacing: 0.04 }}>{t("settings.auditLog.diff", "DIFF")}</span> : null}
          </div>
          <div style={{ fontSize: T.text.label.fontSize, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.target}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: T.space[2], fontSize: T.text.label.fontSize, color: T.n2 }}>
          <span style={{ width: 7, height: 7, borderRadius: T.radius.pill, background: catColor, flexShrink: 0 }} />
          {e.category}
        </div>
        <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: T.soft }}>{e.ip}</span>
        <span style={{ color: T.soft, display: "inline-flex", justifyContent: "center" }}><Icon.chev size={13} dir={open ? "up" : "down"} /></span>
      </div>
      {open ? <AuditDetail e={e} /> : null}
    </>
  );
}

function AuditDetail({ e }: { e: AuditEntry }) {
  const t = useT();
  const ba = beforeAfter(e.payload);
  const sensitive = isSensitive(e);
  const showPayload = hasPayload(e.payload) && !ba;
  return (
    <div style={{ padding: `${T.space[4]} ${T.space[4]} ${T.space[4]}`, background: T.cardSoft, borderBottom: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: T.space[3] }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: `${T.space[3]} ${T.space[4]}` }}>
        <MetaItem label={t("settings.auditLog.actor", "Actor")} value={<>{e.actor.name}{e.actor.email ? <span style={{ color: T.soft }}> · {e.actor.email}</span> : null}</>} />
        <MetaItem label={t("settings.auditLog.role", "Role")} value={e.actor.role || "—"} />
        <MetaItem label={t("settings.auditLog.ipAddress", "IP address")} value={e.ip || "—"} mono />
        <MetaItem label={t("settings.auditLog.timestampUtc", "Timestamp (UTC)")} value={e.occurredAt || "—"} mono />
        <MetaItem label={t("settings.auditLog.actionKey", "Action key")} value={e.actionKey || "—"} mono />
        <MetaItem label={t("settings.auditLog.objectType", "Object type")} value={e.resourceType || "—"} mono />
        <MetaItem label={t("settings.auditLog.objectId", "Object ID")} value={e.resourceId || "—"} mono />
        <MetaItem label={t("settings.auditLog.severity", "Severity")} value={t((AUDIT_SEVERITY[e.severity] || AUDIT_SEVERITY.info).labelKey, (AUDIT_SEVERITY[e.severity] || AUDIT_SEVERITY.info).label)} />
      </div>
      {sensitive && (showPayload || ba) ? <PiiBanner /> : null}
      {ba ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[3] }}>
          <JsonBox label={t("settings.auditLog.previousState", "Previous state")} data={ba.before} empty={t("settings.auditLog.noSnapshotRecorded", "No before/after snapshot was recorded for this entry.")} />
          <JsonBox label={t("settings.auditLog.newState", "New state")} data={ba.after} empty={t("settings.auditLog.noSnapshotRecorded", "No before/after snapshot was recorded for this entry.")} />
        </div>
      ) : showPayload ? (
        <JsonBox label={t("settings.auditLog.payload", "Payload")} data={e.payload} empty={t("settings.auditLog.noPayloadRecorded", "No payload was recorded for this entry.")} />
      ) : sensitive || isMutation(e) ? (
        <div style={{ padding: `${T.space[3]} ${T.space[3]}`, background: T.white, border: `1px dashed ${T.border}`, borderRadius: T.radius.md, fontSize: T.text.label.fontSize, color: T.soft }}>
          {isMutation(e)
            ? t("settings.auditLog.noSnapshotRecorded", "No before/after snapshot was recorded for this entry.")
            : t("settings.auditLog.noPayloadRecorded", "No payload was recorded for this entry.")}
        </div>
      ) : null}
      <div>
        <Btn size="sm" variant="secondary" icon={<Icon.download size={13} />} onClick={() => downloadBlob(`audit-entry-${e.id}.json`, "application/json", jsonPretty(e))}>
          {t("settings.auditLog.downloadRawJson", "Download raw JSON")}
        </Btn>
      </div>
    </div>
  );
}

/* ── Graph API tab rows ──────────────────────────────────────────────────── */
const GRAPH_COLS = "170px 84px 1.9fr 72px 92px 1fr 116px 26px";

function GraphRow({ r, open, onToggle }: { r: GraphApiRequestLog; open: boolean; onToggle: () => void }) {
  const t = useT();
  const mColor = METHOD_COLOR[r.method] || T.n2;
  return (
    <>
      <div onClick={onToggle} style={{ display: "grid", gridTemplateColumns: GRAPH_COLS, padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, alignItems: "center", gap: T.space[2], cursor: "pointer", background: open ? T.cardSoft : T.white }}>
        <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: T.n2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {friendlyDateShort(r.occurredAt)} · {friendlyTime(r.occurredAt)}
        </span>
        <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, fontWeight: 800, color: mColor, background: alpha(mColor, 12), borderRadius: T.radius.sm, padding: `${T.space[1]} ${T.space[2]}`, justifySelf: "start", letterSpacing: 0.03 }}>{r.method}</span>
        <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.url}>{r.path}</span>
        <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, fontWeight: 700, color: statusColor(r.status), display: "inline-flex", alignItems: "center", gap: T.space[2] }}>
          <span style={{ width: 6, height: 6, borderRadius: T.radius.pill, background: statusColor(r.status), flexShrink: 0 }} />{r.status}
        </span>
        <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: r.durationMs >= 1000 ? T.orange1 : T.n2, textAlign: "right" }}>{r.durationMs} {t('settings.auditLog.text')}</span>
        <div style={{ display: "flex", alignItems: "center", gap: T.space[2], minWidth: 0 }}>
          <Avatar size={22} initials={r.actor.init} color={r.actor.color} />
          <span style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.actor.name}</span>
        </div>
        <span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: T.soft }}>{r.ip}</span>
        <span style={{ color: T.soft, display: "inline-flex", justifyContent: "center" }}><Icon.chev size={13} dir={open ? "up" : "down"} /></span>
      </div>
      {open ? (
        <div style={{ padding: `${T.space[4]} ${T.space[4]} ${T.space[4]}`, background: T.cardSoft, borderBottom: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: T.space[3] }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: `${T.space[3]} ${T.space[4]}` }}>
            <MetaItem label={t("settings.auditLog.url", "URL")} value={r.url} mono />
            <MetaItem label={t("settings.auditLog.actor", "Actor")} value={<>{r.actor.name}{r.actor.email ? <span style={{ color: T.soft }}> · {r.actor.email}</span> : null}</>} />
            <MetaItem label={t("settings.auditLog.role", "Role")} value={r.actor.role || "—"} />
            <MetaItem label={t("settings.auditLog.ipAddress", "IP address")} value={r.ip} mono />
            <MetaItem label={t("settings.auditLog.status", "Status")} value={<span style={{ color: statusColor(r.status), fontWeight: 700 }}>{r.status}</span>} mono />
            <MetaItem label={t("settings.auditLog.responseTime", "Response time")} value={`${r.durationMs} ms`} mono />
            <MetaItem label={t("settings.auditLog.objectType", "Object type")} value={r.objectType || "—"} mono />
            <MetaItem label={t("settings.auditLog.timestampUtc", "Timestamp (UTC)")} value={r.occurredAt} mono />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[3] }}>
            <JsonBox label={t("settings.auditLog.requestBody", "Request body")} data={r.requestBody} empty={t("settings.auditLog.noBodySent", "No request body was sent.")} />
            <JsonBox label={t("settings.auditLog.responseBody", "Response body")} data={r.responseBody} empty={t("settings.auditLog.noBodyReturned", "No response body was returned.")} />
          </div>
          <div>
            <Btn size="sm" variant="secondary" icon={<Icon.download size={13} />} onClick={() => downloadBlob(`graph-request-${r.id}.json`, "application/json", jsonPretty(r))}>
              {t("settings.auditLog.downloadRawJson", "Download raw JSON")}
            </Btn>
          </div>
        </div>
      ) : null}
    </>
  );
}

/* ── Screen ──────────────────────────────────────────────────────────────── */
const PAGE_SIZE = 50;
type SysServer = { rows: AuditEntry[]; summary: AuditSummary; pag: PageInfo | null; catCounts: { category: string; count: number }[] | null };

export default function SettingsAuditLog() {
  const t = useT();
  const store = useApprovals(); // re-renders as approval actions land
  const [tab, setTab] = useState<"system" | "graph">("system"); // ui-local
  const [openId, setOpenId] = useState<string | null>(null); // ui-local row expansion

  /* ── System tab state ── */
  const [server, setServer] = useState<SysServer | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [qd, setQd] = useState("");
  const [cat, setCat] = useState("all");
  const [sev, setSev] = useState("all");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: "when", dir: -1 });
  const seq = useRef(0);

  useEffect(() => { const h = window.setTimeout(() => setQd(q), 300); return () => window.clearTimeout(h); }, [q]);
  useEffect(() => { setPage((p) => (p === 1 ? p : 1)); setOpenId(null); }, [qd, cat, sev, sort]);

  // Server sortBy whitelist (kit 7eb21218: occurredAt|actor|action|resourceType).
  // category/ip aren't server-sortable — those headers keep the page-local sort.
  const SYS_SORT_BY: Record<string, string> = { when: "occurredAt", actor: "actor", action: "action" }; // ui-config
  const sysParams = (extra?: Record<string, string>): Record<string, string> => {
    const o: Record<string, string> = { ...extra };
    if (qd.trim()) o.q = qd.trim();
    if (cat !== "all") o.category = cat;
    if (sev !== "all") o.severity = sev;
    const sb = SYS_SORT_BY[sort.key];
    if (sb) { o.sortBy = sb; o.sortDir = sort.dir === 1 ? "asc" : "desc"; }
    return o;
  };
  const fetchLog = (): Promise<boolean> => {
    const fn = (apiClient as Record<string, unknown>).listStudioAuditEvents;
    if (typeof fn !== "function") return Promise.resolve(false);
    const mySeq = ++seq.current;
    return (fn as (p: Record<string, unknown>) => Promise<Record<string, any>>)(sysParams({ page: String(page), limit: String(PAGE_SIZE) }))
      .then((r) => {
        if (mySeq !== seq.current) return true;
        const arr = (r?.auditEvents ?? r?.data ?? r?.rows ?? []) as Record<string, any>[];
        const s = (r?.summary ?? {}) as Record<string, any>;
        const p = (r?.pagination ?? null) as Record<string, any> | null;
        setServer({
          rows: arr.map(fromServer),
          summary: { entries: (s.entriesThisWeek ?? s.totalCount ?? s.entryCount) as number | undefined, flagged: s.flaggedCount as number | undefined, actors: (s.distinctActorCount ?? s.memberCount) as number | undefined, retentionDays: s.retentionDays as number | undefined },
          pag: p ? { page: Number(p.page) || 1, limit: Number(p.limit) || PAGE_SIZE, total: Number(p.total) || arr.length, hasMore: !!p.hasMore } : null,
          catCounts: Array.isArray(r?.categoryCounts) ? (r.categoryCounts as { category: string; count: number }[]) : null,
        });
        return true;
      })
      .catch((e) => { console.warn("listStudioAuditEvents failed:", e); if (mySeq === seq.current) setServer({ rows: [], summary: {}, pag: null, catCounts: null }); return false; });
  };
  useEffect(() => { void fetchLog(); }, [page, qd, cat, sev, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Local approvals trail — the only honest content while the op is absent. */
  const approvalRows: AuditEntry[] = (store.auditEntries ? store.auditEntries() : []).map((r: any, i: number) => {
    const actor = r.actor && typeof r.actor === "object"
      ? { name: r.actor.name || "Approvals", init: r.actor.init || "AP", color: r.actor.color || T.p1, email: r.actor.email || "", role: "" }
      : { name: String(r.actor || "Approvals"), init: String(r.actor || "AP").split(/\s+/).map((w: string) => w[0]).join("").slice(0, 2).toUpperCase(), color: T.p1, email: "", role: "" };
    return {
      id: r.id || "ap-audit-" + i, occurredAt: r.occurredAt || "", day: r.day || "Today", time: r.time || "—", actor,
      action: r.action || "Approval activity", actionKey: r.actionKey || "approval.activity", category: "Approvals",
      target: r.target || "", ip: r.ip || "—", severity: (r.severity || "info") as AuditEntry["severity"],
      resourceType: "approval", resourceId: null, payload: null,
    };
  });
  const fallbackFiltered = approvalRows.filter((e) => {
    if (cat !== "all" && e.category !== cat) return false;
    if (sev !== "all" && e.severity !== sev) return false;
    if (qd.trim()) {
      const n = qd.trim().toLowerCase();
      if (!`${e.actor.name} ${e.action} ${e.target} ${e.category} ${e.ip}`.toLowerCase().includes(n)) return false;
    }
    return true;
  });

  const log: AuditEntry[] = server ? server.rows : fallbackFiltered;
  const summary: AuditSummary = server?.summary ?? {};
  const sysPag: PageInfo = server?.pag ?? {
    page, limit: PAGE_SIZE, total: log.length,
    hasMore: page * PAGE_SIZE < log.length,
  };
  const pageRows = server?.pag ? log : log.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const sortedRows = useMemo(() => {
    const val = (e: AuditEntry): string =>
      sort.key === "actor" ? e.actor.name : sort.key === "action" ? e.action : sort.key === "category" ? e.category : sort.key === "ip" ? e.ip : (e.occurredAt || `${e.day} ${e.time}`);
    return [...pageRows].sort((a, b) => val(a).localeCompare(val(b)) * sort.dir);
  }, [pageRows, sort]);

  const categories = useMemo(() => {
    if (server?.catCounts) return server.catCounts.map((c) => ({ id: c.category, count: c.count })).filter((c) => c.count > 0).sort((a, b) => a.id.localeCompare(b.id));
    const counts: Record<string, number> = {};
    log.forEach((e) => { counts[e.category] = (counts[e.category] || 0) + 1; });
    return Object.keys(counts).sort().map((k) => ({ id: k, count: counts[k] }));
  }, [server, log]);

  const alerts = log.filter((e) => e.severity !== "info").length;

  const fetchAllSys = async (): Promise<AuditEntry[]> => {
    const fn = (apiClient as Record<string, unknown>).listStudioAuditEvents;
    if (typeof fn !== "function") return log;
    const r = await (fn as (p: Record<string, unknown>) => Promise<Record<string, any>>)(sysParams({ page: "1", limit: "200" }));
    return ((r?.auditEvents ?? r?.data ?? []) as Record<string, any>[]).map(fromServer);
  };
  const exportSystem = async (kind: ExportKind): Promise<void> => {
    setExporting(true);
    try {
      const csvFn = (apiClient as Record<string, unknown>).exportStudioAuditEventsCsv;
      if (kind === "csv" && typeof csvFn === "function") {
        const text = await (csvFn as (p: Record<string, unknown>) => Promise<string>)(sysParams());
        downloadBlob("audit-log.csv", "text/csv;charset=utf-8", text.startsWith("\uFEFF") ? text : "\uFEFF" + text);
      } else {
        const all = await fetchAllSys();
        if (kind === "json") downloadBlob("audit-log.json", "application/json", jsonPretty(all));
        else {
          const head = ["Timestamp (UTC)", "Actor", "Email", "Role", "Action", "Action key", "Category", "Details", "Object type", "Object ID", "Source IP", "Severity"];
          const rows = all.map((e) => [e.occurredAt || `${e.day} ${e.time}`, e.actor.name, e.actor.email, e.actor.role, e.action, e.actionKey, e.category, e.target, e.resourceType, e.resourceId ?? "", e.ip, e.severity]);
          if (kind === "csv") downloadBlob("audit-log.csv", "text/csv;charset=utf-8", csvOf(head, rows));
          else downloadBlob("audit-log.xls", "application/vnd.ms-excel", xlsOf("Audit log", head, rows));
        }
      }
      toast({ tone: "success", title: i18nT("settings.auditLog.auditLogExported", "Audit log exported"), subtitle: t("settings.auditLog.yourDownloadIsReady", "Your download is ready.") });
    } catch (e) {
      console.warn("audit export failed:", e);
      toast({ tone: "warning", title: t("settings.auditLog.exportFailed", "Export failed"), subtitle: t("settings.auditLog.couldnTPrepareTheExport", "Couldn't prepare the export. Try again.") });
    } finally { setExporting(false); }
  };

  /* ── Graph API tab state ── */
  const [graph, setGraph] = useState<GraphApiRequestLogList | null>(null);
  const [gErr, setGErr] = useState(false);
  const [gPage, setGPage] = useState(1);
  const [gQ, setGQ] = useState("");
  const [gQd, setGQd] = useState("");
  const [gMethod, setGMethod] = useState<GraphApiMethod | "all">("all");
  const [gSort, setGSort] = useState<{ key: string; dir: 1 | -1 }>({ key: "occurredAt", dir: -1 });
  const gSeq = useRef(0);

  useEffect(() => { const h = window.setTimeout(() => setGQd(gQ), 300); return () => window.clearTimeout(h); }, [gQ]);
  useEffect(() => { setGPage((p) => (p === 1 ? p : 1)); setOpenId(null); }, [gQd, gMethod]);
  useEffect(() => {
    if (tab !== "graph") return;
    const mySeq = ++gSeq.current;
    setGErr(false);
    listGraphApiRequestLogs({ page: gPage, limit: PAGE_SIZE, q: gQd.trim() || undefined, method: gMethod, sort: gSort.key, dir: gSort.dir === 1 ? "asc" : "desc" })
      .then((r) => { if (mySeq === gSeq.current) setGraph(r); })
      .catch((e) => { console.warn("listGraphApiRequestLogs failed:", e); if (mySeq === gSeq.current) { setGraph(null); setGErr(true); } });
  }, [tab, gPage, gQd, gMethod, gSort]);

  const exportGraph = async (kind: ExportKind): Promise<void> => {
    setExporting(true);
    try {
      // §2.9 (2026-07-26): page through the WHOLE filtered log — the old
      // single {page:1, limit:200} call silently truncated larger logs while
      // toasting unqualified success. 50-page (10k-row) safety valve, labeled
      // in the toast + filename if ever hit.
      const args = { limit: 200, q: gQd.trim() || undefined, method: gMethod, sort: gSort.key, dir: gSort.dir === 1 ? ("asc" as const) : ("desc" as const) };
      const first = await listGraphApiRequestLogs({ page: 1, ...args });
      const data = [...first.data];
      const total = (first as { total?: number }).total;
      const PAGE_CAP = 50;
      let page = 2;
      while (page <= PAGE_CAP && (total != null ? data.length < total : data.length === (page - 1) * 200)) {
        const r2 = await listGraphApiRequestLogs({ page, ...args });
        if (!r2.data.length) break;
        data.push(...r2.data);
        page++;
      }
      const capped = page > PAGE_CAP && (total != null ? data.length < total : true);
      const stem = capped ? "graph-api-log-first-10000" : "graph-api-log";
      if (kind === "json") downloadBlob(`${stem}.json`, "application/json", jsonPretty(data));
      else {
        const head = ["Timestamp (UTC)", "Method", "URL", "Status", "Response time (ms)", "Actor", "Email", "Role", "IP", "Object type", "Object ID", "Request body", "Response body"];
        const rows = data.map((x) => [x.occurredAt, x.method, x.url, x.status, x.durationMs, x.actor.name, x.actor.email ?? "", x.actor.role, x.ip, x.objectType, x.objectId ?? "", x.requestBody == null ? "" : jsonPretty(x.requestBody), x.responseBody == null ? "" : jsonPretty(x.responseBody)]);
        if (kind === "csv") downloadBlob(`${stem}.csv`, "text/csv;charset=utf-8", csvOf(head, rows));
        else downloadBlob(`${stem}.xls`, "application/vnd.ms-excel", xlsOf("Graph API log", head, rows));
      }
      toast({ tone: "success", title: t("settings.auditLog.graphApiLogExported", "Graph API log exported"), subtitle: capped ? t("settings.auditLog.exportCappedFirst10000", "First 10,000 rows — narrow the filters to export the rest.") : t("settings.auditLog.yourDownloadIsReady", "Your download is ready.") });
    } catch (e) {
      console.warn("graph export failed:", e);
      toast({ tone: "warning", title: t("settings.auditLog.exportFailed", "Export failed"), subtitle: t("settings.auditLog.couldnTPrepareTheExport", "Couldn't prepare the export. Try again.") });
    } finally { setExporting(false); }
  };

  /* ── Shared UI bits ── */
  const onSort = (setter: (u: (s: { key: string; dir: 1 | -1 }) => { key: string; dir: 1 | -1 }) => void, descFirst: string[]) => (id: string) =>
    setter((s) => (s.key === id ? { key: id, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { key: id, dir: (descFirst.includes(id) ? -1 : 1) as 1 | -1 }));

  const segBtn = (id: string, label: string, active: boolean, onClick: () => void) => (
    // ui-raw: neutral-white-active segment (bg:T.white/color:T.n1) — jrni-ui's
    // SegmentedControl exists but its active state is brand-tinted (bg:T.p3/
    // color:T.p1), a different visual treatment; swapping it in would restyle
    // this tab, not just swap the primitive.
    <button key={id} onClick={onClick} style={{
      padding: `${T.space[2]} ${T.space[4]}`, border: "none", cursor: "pointer",
      background: active ? T.white : "transparent", color: active ? T.n1 : T.soft,
      fontFamily: T.font, fontSize: T.text.bodySm.fontSize, fontWeight: 600, borderRadius: T.radius.control,
      boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
    }}>{label}</button>
  );

  const catChip = (id: string, label: string, count: number) => {
    const active = cat === id;
    const color = id === "all" ? T.p1 : (AUDIT_CATEGORY_COLOR[id] || T.p1);
    return (
      // ui-raw: pill filter chip whose active fill is a PER-CATEGORY dynamic
      // color (AUDIT_CATEGORY_COLOR[id]) — jrni-ui's ChipToggleGroup only
      // supports one fixed brand-tint active color across all options, so it
      // can't reproduce this per-option coloring without losing the distinct
      // per-category legend.
      <button key={id} onClick={() => setCat(id)} style={{
        padding: `${T.space[1]} ${T.space[3]}`, borderRadius: T.radius.pill, cursor: "pointer",
        border: `1px solid ${active ? color : T.border}`,
        background: active ? color : T.white, color: active ? T.white : T.n2,
        fontFamily: T.font, fontSize: T.text.label.fontSize, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: T.space[2],
      }}>
        {id !== "all" ? <span style={{ width: 7, height: 7, borderRadius: T.radius.pill, background: active ? T.white : color }} /> : null}
        {label}<span style={{ opacity: 0.7, fontWeight: 500 }}>{count}</span>
      </button>
    );
  };
  const methodChip = (id: GraphApiMethod | "all", label: string) => {
    const active = gMethod === id;
    const color = id === "all" ? T.p1 : METHOD_COLOR[id];
    return (
      // ui-raw: same per-option dynamic-color pattern as catChip above (here
      // keyed by HTTP method via METHOD_COLOR) — not reproducible with
      // ChipToggleGroup's single fixed active color.
      <button key={id} onClick={() => setGMethod(id)} style={{
        padding: `${T.space[1]} ${T.space[3]}`, borderRadius: T.radius.pill, cursor: "pointer",
        border: `1px solid ${active ? color : T.border}`,
        background: active ? color : T.white, color: active ? T.white : id === "all" ? T.n2 : color,
        fontFamily: T.fontMono, fontSize: T.text.label.fontSize, fontWeight: 800, letterSpacing: 0.03,
      }}>{label}</button>
    );
  };
  const sevBtn = (id: string, label: string) => {
    const active = sev === id;
    return (
      // ui-raw: same neutral-white-active segment treatment as segBtn above —
      // see that comment (SegmentedControl's tinted active state doesn't match).
      <button key={id} onClick={() => setSev(id)} style={{
        padding: `${T.space[2]} ${T.space[3]}`, border: "none", cursor: "pointer",
        background: active ? T.white : "transparent", color: active ? T.n1 : T.soft,
        fontFamily: T.font, fontSize: T.text.label.fontSize, fontWeight: 600, borderRadius: T.radius.control,
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
      }}>{label}</button>
    );
  };

  const headCell: CSSProperties = { display: "grid", padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, background: T.cardSoft, gap: T.space[2], alignItems: "center" };
  const sysSort = onSort(setSort, ["when"]);
  const graphSort = onSort(setGSort, ["occurredAt", "durationMs", "status"]);

  return (
    <SettingsShell activeTab="audit">
      <PageHeader
        title={t("settings.auditLog.auditLog", "Audit log")}
        subtitle={tab === "system"
          ? t("settings.auditLog.everyAdministrativeActionWhoActedFromWhichIp", "Every administrative action across the workspace — who acted, from which IP address, and what changed. Entries are immutable.")
          : t("settings.auditLog.everyRequestToTheGraphApi", "Every request to the Graph API — method, URL, status code, server response time, and the full request and response bodies.")}
        actions={<>
          {tab === "system" ? <TamperCheck /> : null}
          {tab === "system" ? (
            <Btn variant="secondary" icon={<Icon.refresh size={14} />} disabled={refreshing} onClick={() => { void (async () => { setRefreshing(true); const ok = await fetchLog(); setRefreshing(false); toast(ok ? { tone: "info", title: t("settings.auditLog.auditLogRefreshed", "Audit log refreshed"), subtitle: t("settings.auditLog.youReViewingTheLatestActivity", "You're viewing the latest activity.") } : { tone: "warning", title: t("settings.auditLog.couldnTRefresh", "Couldn't refresh"), subtitle: t("settings.auditLog.theAuditServiceIsnTReachableRightNow", "The audit service isn't reachable right now.") }); })(); }}>{refreshing ? t("settings.settingsAuditLog.refreshing", "Refreshing…") : t("common.actions.refresh", "Refresh")}</Btn>
          ) : null}
          <ExportMenu busy={exporting} onPick={(k) => { void (tab === "system" ? exportSystem(k) : exportGraph(k)); }} />
        </>}
      />
      <DataSourceStrip screen="settings-audit" ops={["listGraphApiRequestLogs"]} reqId="REQ-061" />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: `${T.space[4]} ${T.space[6]} ${T.space[6]}`, display: "flex", flexDirection: "column", gap: T.space[4] }}>

        {/* Tabs */}
        <div style={{ display: "flex", gap: T.space[1], padding: T.space[1], background: T.n6, borderRadius: T.radius.md, alignSelf: "flex-start" }}>
          {segBtn("system", t("settings.auditLog.system", "System"), tab === "system", () => { setTab("system"); setOpenId(null); })}
          {segBtn("graph", t("settings.auditLog.graphApi", "Graph API"), tab === "graph", () => { setTab("graph"); setOpenId(null); })}
        </div>

        {tab === "system" ? (
          <>
            {/* Summary tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: T.space[3] }}>
              <StatTile label={t("settings.auditLog.entriesThisWeek", "Entries (this week)")} value={String(summary.entries ?? sysPag.total)} delta={`Across ${summary.actors ?? new Set(log.map((e) => e.actor.name)).size} members`} icon={<Icon.list size={14} />} />
              <StatTile label={t("settings.auditLog.flaggedForReview", "Flagged for review")} value={String(summary.flagged ?? alerts)} delta={(summary.flagged ?? alerts) > 0 ? "Warnings + critical" : "All clear"} accent={T.orange1} icon={<Icon.warn size={14} />} />
              <StatTile label={t("settings.auditLog.retention", "Retention")} value={summary.retentionDays ? `${summary.retentionDays} days` : "—"} delta="Compliance default" accent={T.green1} icon={<Icon.clock size={14} />} />
            </div>

            {/* Filters */}
            <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
              <div style={{ display: "flex", gap: T.space[2], alignItems: "center" }}>
                <Input full size="sm" leading={<Icon.search size={12} />} placeholder={t("settings.auditLog.searchByActorActionObjectTypeOrIp", "Search by actor, action, object type, target, or IP…")} value={q} onChange={(e) => setQ(e.target.value)} />
                <div style={{ display: "flex", gap: T.space[1], padding: T.space[1], background: T.n6, borderRadius: T.radius.md, flexShrink: 0 }}>
                  {sevBtn("all", t("settings.auditLog.all", "All"))}
                  {sevBtn("warn", t("settings.auditLog.warnings", "Warnings"))}
                  {sevBtn("critical", t("settings.auditLog.critical", "Critical"))}
                </div>
              </div>
              <div style={{ display: "flex", gap: T.space[2], flexWrap: "wrap" }}>
                {catChip("all", t("settings.auditLog.allCategories", "All categories"), sysPag.total)}
                {categories.map((c) => catChip(c.id, c.id, c.count))}
              </div>
            </div>

            {/* Table */}
            <Card style={{ padding: 0, overflow: "hidden", flexShrink: 0 }}>
              <div style={{ ...headCell, gridTemplateColumns: AUDIT_COLS }}>
                <Th id="when" label={t("settings.auditLog.when", "When")} sort={sort} onSort={sysSort} />
                <Th id="actor" label={t("settings.auditLog.actor", "Actor")} sort={sort} onSort={sysSort} />
                <Th id="action" label={t("settings.auditLog.action", "Action")} sort={sort} onSort={sysSort} />
                <Th id="category" label={t("settings.auditLog.category", "Category")} sort={sort} onSort={sysSort} />
                <Th id="ip" label={t("settings.auditLog.sourceIp", "Source IP")} sort={sort} onSort={sysSort} />
                <span />
              </div>
              {sortedRows.length === 0 ? (
                <div style={{ padding: T.space[10], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>
                  {sysPag.total === 0 && !qd && cat === "all" && sev === "all"
                    ? t("settings.auditLog.noAuditEntriesYet", "No audit entries yet — administrative actions will appear here as they happen.")
                    : t("settings.auditLog.noAuditEntriesMatchTheseFilters", "No audit entries match these filters.")}
                </div>
              ) : sortedRows.map((e) => (
                <AuditRow key={e.id} e={e} open={openId === e.id} onToggle={() => setOpenId(openId === e.id ? null : e.id)} />
              ))}
              <Pager pag={sysPag} onPage={(p) => { setPage(p); setOpenId(null); }} />
            </Card>

            <div style={{ fontSize: T.text.label.fontSize, color: T.soft, textAlign: "center", padding: `${T.space[1]} 0` }}>
              {t("settings.auditLog.onSiteRowsAreIngestedLiveFromTheEventDayApp", "· On-site rows are ingested live from the Event Day app.")}
            </div>
          </>
        ) : (
          <>
            {/* Graph summary tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: T.space[3] }}>
              <StatTile label={t("settings.auditLog.requestsFiltered", "Requests (filtered)")} value={String(graph?.summary.total ?? "—")} delta="GET · POST · PUT · PATCH · DELETE" icon={<Icon.network size={14} />} />
              <StatTile label={t("settings.auditLog.errorResponses", "Error responses")} value={String(graph?.summary.errorCount ?? "—")} delta={graph && graph.summary.total > 0 ? `${Math.round((graph.summary.errorCount / graph.summary.total) * 100)}% of requests` : "—"} accent={T.red1} icon={<Icon.warn size={14} />} />
              <StatTile label={t("settings.auditLog.avgResponseTime", "Avg response time")} value={graph ? `${graph.summary.avgDurationMs} ms` : "—"} delta="Server-side" accent={T.green1} icon={<Icon.clock size={14} />} />
            </div>

            {/* Filters */}
            <div style={{ display: "flex", gap: T.space[2], alignItems: "center" }}>
              <Input full size="sm" leading={<Icon.search size={12} />} placeholder={t("settings.auditLog.searchByActorMethodUrlStatusOrObjectType", "Search by actor, method, URL, status code, or object type…")} value={gQ} onChange={(e) => setGQ(e.target.value)} />
              <div style={{ display: "flex", gap: T.space[2], flexShrink: 0 }}>
                {methodChip("all", t("settings.auditLog.all", "All"))}
                {methodChip("GET", "GET")}
                {methodChip("POST", "POST")}
                {methodChip("PUT", "PUT")}
                {methodChip("PATCH", "PATCH")}
                {methodChip("DELETE", "DELETE")}
              </div>
            </div>

            {/* Table */}
            <Card style={{ padding: 0, overflow: "hidden", flexShrink: 0 }}>
              <div style={{ ...headCell, gridTemplateColumns: GRAPH_COLS }}>
                <Th id="occurredAt" label={t("settings.auditLog.when", "When")} sort={gSort} onSort={graphSort} />
                <Th id="method" label={t("settings.auditLog.method", "Method")} sort={gSort} onSort={graphSort} />
                <Th id="url" label={t("settings.auditLog.url", "URL")} sort={gSort} onSort={graphSort} />
                <Th id="status" label={t("settings.auditLog.status", "Status")} sort={gSort} onSort={graphSort} />
                <Th id="durationMs" label={t("settings.auditLog.duration", "Duration")} sort={gSort} onSort={graphSort} />
                <Th id="actor" label={t("settings.auditLog.actor", "Actor")} sort={gSort} onSort={graphSort} />
                <span style={{ fontSize: T.text.label.fontSize, fontWeight: 600, letterSpacing: 0.04, textTransform: "uppercase", color: T.soft }}>{t("settings.auditLog.sourceIp", "Source IP")}</span>
                <span />
              </div>
              {gErr ? (
                <div style={{ padding: T.space[10], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>
                  {t("settings.auditLog.theGraphApiRequestLogIsnTReachableRightNow", "The Graph API request log isn't reachable right now.")}
                </div>
              ) : !graph ? (
                <div style={{ padding: T.space[10], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>{t("settings.auditLog.loading", "Loading…")}</div>
              ) : graph.data.length === 0 ? (
                <div style={{ padding: T.space[10], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>
                  {t("settings.auditLog.noGraphApiRequestsMatchTheseFilters", "No Graph API requests match these filters.")}
                </div>
              ) : graph.data.map((r) => (
                <GraphRow key={r.id} r={r} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} />
              ))}
              {graph ? <Pager pag={graph.pagination} onPage={(p) => { setGPage(p); setOpenId(null); }} /> : null}
            </Card>
          </>
        )}
      </div>
    </SettingsShell>
  );
}
